import React from 'react'

import {extractNarrativeNameList} from '../../../../modes/attendance/attendanceNameList'
import {
  hasPackedNameLists,
  hasStructuredAttendanceView,
} from '../../../../modes/attendance/attendanceV2ResultTable'
import {AttendanceV2Payload, getCompoundParts} from '../../../../modes/attendance/attendanceV2Types'
import {AttendanceChatSendExtras} from '../../../../modes/attendance/attendanceWizardTypes'
import {condenseAttendanceNarrative} from '../../../../modes/attendance/condenseAttendanceNarrative'
import AttendanceV2AnswerBody from './AttendanceV2AnswerBody'
import AttendanceV2CompoundView from './AttendanceV2CompoundView'
import AttendanceV2DisambiguationPicker from './AttendanceV2DisambiguationPicker'
import AttendanceV2NameListSection from './AttendanceV2NameListSection'
import AttendanceV2NeedsInputView from './AttendanceV2NeedsInputView'

/** Drops "1. …" / "2) …" enumerated lines from a narrative while keeping the surrounding prose. Used
 *  to strip the candidate list a disambiguation narrative repeats, since the picker below renders
 *  those same candidates as selectable cards. */
function stripEnumeratedListLines(text: string): string {
  return text
    .split('\n')
    .filter((line) => !/^\s*\d+[.)]\s/.test(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

type AttendanceV2MessageBodyProps = {
  narrative: string
  payload: AttendanceV2Payload
  theme: 'light' | 'dark'
  attendanceContext?: Record<string, unknown> | null
  renderHtmlBody: (text: string, ref?: React.RefObject<HTMLDivElement | null>) => React.ReactNode
  narrativeRef?: React.RefObject<HTMLDivElement | null>
  onFollowUp: (query: string, extras?: AttendanceChatSendExtras) => void
  tableUnavailableHint?: boolean
}

const AttendanceV2MessageBody: React.FC<AttendanceV2MessageBodyProps> = ({
  narrative,
  payload,
  theme,
  attendanceContext,
  renderHtmlBody,
  narrativeRef,
  onFollowUp,
  tableUnavailableHint = false,
}) => {
  const rawHeadline = narrative?.trim() || payload.narrative?.trim() || ''
  const parts = getCompoundParts(payload)
  const showDisambiguationPicker =
    payload.kind === 'disambiguation' && Boolean(payload.disambiguation?.options?.length)
  // The picker renders each candidate as a card, so drop the numbered candidate list the
  // disambiguation narrative repeats and keep only the lead question.
  const baseHeadline = showDisambiguationPicker
    ? stripEnumeratedListLines(rawHeadline)
    : rawHeadline
  // A sentence that enumerates fifty names is the same list the table draws — or, when the data
  // carries no such column, the list section below draws. Either way the sentence comes out of
  // the prose so the names appear once, in a form that can be scanned.
  const narrativeNameList = parts.length ? null : extractNarrativeNameList(baseHeadline)
  const showNarrativeNameList = Boolean(narrativeNameList) && !hasPackedNameLists(payload)
  // A compound narrative is just its parts concatenated, and each part renders its own prose —
  // keeping the top-level copy would print the whole answer twice.
  const headline = parts.length
    ? ''
    : condenseAttendanceNarrative(narrativeNameList?.text ?? baseHeadline, {
        hasStructuredView: hasStructuredAttendanceView(payload),
      })
  const errorText = payload.error?.trim() || rawHeadline || 'Something went wrong.'
  const showHeadline = Boolean(headline) && payload.kind !== 'error'

  return (
    <div>
      {showHeadline ? <div className='mb-1'>{renderHtmlBody(headline, narrativeRef)}</div> : null}

      {showNarrativeNameList && narrativeNameList ? (
        <AttendanceV2NameListSection
          names={narrativeNameList.names}
          subject={narrativeNameList.subject}
          theme={theme}
        />
      ) : null}

      {parts.length ? (
        <AttendanceV2CompoundView
          parts={parts}
          theme={theme}
          renderHtmlBody={renderHtmlBody}
          narrativeRef={narrativeRef}
        />
      ) : (
        <AttendanceV2AnswerBody
          payload={payload}
          theme={theme}
          tableUnavailableHint={tableUnavailableHint}
        />
      )}

      {payload.kind === 'disambiguation' && payload.disambiguation ? (
        <AttendanceV2DisambiguationPicker
          field={payload.disambiguation.field}
          options={payload.disambiguation.options}
          theme={theme}
          onSelect={(query, extras) =>
            onFollowUp(query, {
              ...extras,
              ...(attendanceContext ? {attendanceContext} : {}),
            })
          }
        />
      ) : null}

      {payload.kind === 'needs_input' && payload.needsInput ? (
        <AttendanceV2NeedsInputView
          fields={payload.needsInput.fields}
          theme={theme}
          onChipSelect={onFollowUp}
        />
      ) : null}

      {payload.kind === 'error' ? (
        <div
          className={showHeadline ? 'mt-1' : undefined}
          style={{
            color: theme === 'dark' ? '#f1416c' : '#d9214e',
            fontSize: '0.9rem',
          }}
        >
          {renderHtmlBody(errorText)}
        </div>
      ) : null}
    </div>
  )
}

export default AttendanceV2MessageBody
